
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";

interface ResetPasswordFormProps {
  onModeChange: (mode: "signin") => void;
}

export const ResetPasswordForm = ({ onModeChange }: ResetPasswordFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isLoading || !email.trim() || isRateLimited) {
      return;
    }
    
    setIsLoading(true);
    setEmailSent(false);

    try {
      const trimmedEmail = email.trim().toLowerCase();
      
      // Get the current URL's origin for the redirect
      const origin = window.location.origin;
      const redirectTo = `${origin}/auth/update-password`;
      
      console.log("Starting password reset process:", {
        email: trimmedEmail,
        redirectTo
      });

      // Show immediate feedback
      toast({
        title: "Processing",
        description: "Sending reset link...",
      });

      const { error } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
        redirectTo
      });

      if (error) {
        console.error("Password reset error details:", {
          message: error.message,
          status: error.status,
          name: error.name,
          stack: error.stack
        });
        
        if (error.message?.includes("rate limit") || error.status === 429) {
          setIsRateLimited(true);
          setTimeout(() => setIsRateLimited(false), 3600000); // 1 hour
          throw new Error("Too many reset attempts. Please try again in 60 minutes.");
        }

        if (error.status === 422) {
          throw new Error("Please enter a valid email address.");
        }

        throw error;
      }

      // If we get here, send the custom email
      try {
        await supabase.functions.invoke('send-reset-email', {
          body: {
            email: trimmedEmail,
            resetLink: redirectTo // Supabase will handle the token
          }
        });
      } catch (emailError) {
        console.error("Error sending custom reset email:", emailError);
        // We don't throw here as the reset process was successful
      }

      setEmailSent(true);
      toast({
        title: "Reset Link Sent",
        description: "If an account exists with this email, you will receive a password reset link. Please check both your inbox and spam folder.",
      });
      
    } catch (error: any) {
      console.error("Password reset error:", error);
      toast({
        title: "Error",
        description: error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
      setEmailSent(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading || isRateLimited}
            required
          />
        </div>
      </div>

      {isLoading && (
        <Alert>
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          <AlertDescription>
            Sending reset link... This may take a few moments.
          </AlertDescription>
        </Alert>
      )}

      {emailSent && (
        <div className="text-sm text-green-600 bg-green-50 p-3 rounded-md space-y-2">
          <p>If an account exists with this email, you will receive a reset link shortly.</p>
          <p className="font-medium">Important:</p>
          <ul className="list-disc pl-4 space-y-1">
            <li>Check both your inbox and spam folder</li>
            <li>The link may take a few minutes to arrive</li>
            <li>The reset link will expire in 24 hours</li>
          </ul>
          <p className="mt-2">Haven't received the email? Try these steps:</p>
          <ul className="list-disc pl-4 space-y-1">
            <li>Double-check your email address for typos</li>
            <li>Check your spam/junk folder again after a few minutes</li>
            <li>Try sending another reset link after 5 minutes</li>
          </ul>
        </div>
      )}

      {isRateLimited && (
        <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
          Password reset is temporarily disabled due to too many attempts. 
          Please try again in 60 minutes or contact support if you need immediate assistance.
        </div>
      )}

      <Button 
        type="submit" 
        className="w-full" 
        disabled={isLoading || !email.trim() || isRateLimited}
      >
        {isRateLimited 
          ? "Please wait 60 minutes" 
          : isLoading 
            ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Sending Reset Link...
              </>
            )
            : "Send Reset Link"
        }
      </Button>

      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={() => !isLoading && onModeChange("signin")}
          className="text-sm text-blue-600 hover:underline disabled:opacity-50"
          disabled={isLoading}
        >
          Back to sign in
        </button>
      </div>
    </form>
  );
};
